import React, { useState } from 'react';
import { observer } from 'mobx-react';
import A from '@this/components/shared/atoms/a';
import {
  MappingFormWrapper,
  MappingFormLabel,
  MappingFormDetailArea,
  MappingFormDetailIndentedArea,
  MappingFormDetailItemArea,
  MappingFormDetailSelectArea,
  MappingFormDetailLinkArea
} from '@this/components/trips_management/trips/request_approval_modal/mapping_form.style';
import type { FilterableSelectorOption } from '@this/shared/filterable_selector/filterable_selector';
import FilterableSelector from '@this/shared/filterable_selector/filterable_selector';
import type Department from '@this/domain/department/department';
import type OrderItemMapping from '@this/domain/order_item_mapping';
import type ArrangementRequestWfInfo from '@this/src/domain/arrangement_request_info/arrangement_request_wf_info';

interface Props {
  wf: ArrangementRequestWfInfo;
}

const ChargingDepartmentMappingForm: React.FC<Props> = ({ wf }) => {
  const [showDetail, setShowDetail] = useState(wf.isForDepartmentDetail());

  const handleSelectDepartmentDetail = (
    changingMapping: OrderItemMapping,
    option: FilterableSelectorOption | null
  ) => {
    const values = option?.value.split('-'); // e.g. department-1
    if (values && values?.length === 2) {
      if (values[0] === 'department') {
        changingMapping.setChargingDepartmentId(Number(values[1]));
      } else if (values[0] === 'share') {
        changingMapping.setChargingDepartmentShareId(Number(values[1]));
      }
    }
  };

  const handleSelect = (selected: FilterableSelectorOption | null): void => {
    const values = selected?.value.split('-'); // e.g. department-1
    if (values && values?.length === 2 && values[0] === 'department') {
      wf.chargingDepartmentId = values[1];
    } else {
      wf.chargingDepartmentId = '';
    }
    wf.orderItems.forEach(item => {
      item.orderItemMappings.list.forEach(mapping => handleSelectDepartmentDetail(mapping, selected));
    });
    wf.resetExpensesAccountTypes();
  };

  const createOption = (department: Department) => ({
    label: department.descriptionWithCode(),
    value: `department-${department.id.toString()}`,
    matcher: department.descriptionWithCode()
  });

  const handleShowDepartmentDetailClick = (value: boolean) => {
    if (!value) wf.resetMappingsDepartmentId();
    setShowDetail(value);
    wf.showDepartmentDetail = value;
  };

  const departmentOption = () => {
    return wf.departments.list.map(d => ({
      label: d.descriptionWithCode(),
      value: `department-${d.id.toString()}`,
      matcher: d.descriptionWithCode()
    }));
  };

  const departmentDetailOption = (): any[] => {
    const departmentsOption: any[] = wf.departments.list.map(d => {
      return {
        label: d.descriptionWithCode(),
        value: `department-${d.id.toString()}`,
        matcher: d.descriptionWithCode()
      };
    });
    const sharesOption: any[] = wf.departmentShares.list.map(s => {
      return {
        labelLines: s.codeAndNameLines(),
        value: `share-${s.id.toString()}`,
        matcher: s.codeAndNameOneLine()
      };
    });
    return departmentsOption.concat(sharesOption);
  };

  return (
    <MappingFormWrapper>
      {showDetail ? (
        <MappingFormDetailArea>
          {wf.orderItems.map((item, item_index) => (
            <div key={`item${item.id}`}>
              <MappingFormLabel>
                {item_index + 1}. {item?.categoryStr()}
              </MappingFormLabel>
              <MappingFormDetailIndentedArea>
                {item.travelerInformations.map((traveler: any) => (
                  <MappingFormDetailItemArea key={traveler.id}>
                    <MappingFormLabel>{traveler.name}</MappingFormLabel>
                    <MappingFormDetailSelectArea>
                      <FilterableSelector
                        placeholder="部署を検索"
                        options={departmentDetailOption()}
                        selected={{
                          label:
                            item.orderItemMappings
                              .findByTravelerInformationAndOrderItem(traveler.id, item.id)
                              ?.chargingDepartmentDescription(wf.departments, wf.departmentShares) || '',
                          value: '',
                          matcher: ''
                        }}
                        onSelect={option => {
                          const mapping = item.orderItemMappings.findByTravelerInformationAndOrderItem(
                            traveler.id,
                            item.id
                          );
                          if (mapping) {
                            handleSelectDepartmentDetail(mapping, option);
                          }
                        }}
                      />
                    </MappingFormDetailSelectArea>
                  </MappingFormDetailItemArea>
                ))}
              </MappingFormDetailIndentedArea>
            </div>
          ))}
        </MappingFormDetailArea>
      ) : (
        <div>
          <FilterableSelector
            placeholder="部署を検索"
            options={departmentOption()}
            selected={wf.chargingDepartment ? createOption(wf.chargingDepartment) : null}
            onSelect={handleSelect}
          />
        </div>
      )}
      {wf.projectShareAvailability &&
        (showDetail ? (
          <MappingFormDetailLinkArea>
            <A onClick={() => handleShowDepartmentDetailClick(false)}>通常設定へ</A>
          </MappingFormDetailLinkArea>
        ) : (
          <MappingFormDetailLinkArea>
            <A onClick={() => handleShowDepartmentDetailClick(true)}>詳細設定へ</A>
          </MappingFormDetailLinkArea>
        ))}
    </MappingFormWrapper>
  );
};

export default observer(ChargingDepartmentMappingForm);
